import { ZIP } from '~/utils/helper/zip-stream';
import type { TResource } from '~/api/oneBoxV3/oneBoxV3.type';
import type { TDownloadableResource } from '~/types/common.type';
import type {
  BatchZipFileFailedPayload,
  BatchZipStalledPayload,
  BatchZipStartedPayload,
  BatchZipTerminalPayload,
} from '~/tracking/type';

/**
 * 網頁版「全部下載」的觀測（NBX3-954，Sentry NANI-BOX3-47 方案 A）。
 *
 * 這一批失敗在 Sentry 上全是沒有訊息的 `undefined`，看不出斷在哪一步；網頁版又只報失敗
 * 不報成功，算不出失敗率；還有一類「卡住」完全不會產生錯誤。這裡把每一批的開始、結果、
 * 單檔失敗、停滯都回報出來，讓後續要修哪一項（B1～E）依數據決定。
 *
 * 只加回報，不改下載行為：檔案失敗時串流照舊卡住、HTTP 錯誤頁照舊被打包。兩者都是已知
 * 問題（方案 B1），數據必須反映現行行為，B1 才有依據。
 */

export type BatchZipStage = 'read' | 'write' | 'close';

/** 固定的低基數集合，直接拿來當 Sentry fingerprint，不能放任意錯誤訊息進去 */
export type BatchZipErrorKind =
  | 'nullish'
  | 'error'
  | 'dom-exception'
  | 'string'
  | 'object'
  | 'other-primitive';

export type BatchZipFileFailReason =
  'fetch_reject' | 'null_body' | 'duplicate_name' | 'other';

export type StreamSaverMode = 'transferable' | 'fallback' | 'blob';

const DEFAULT_DOWNLOAD_NAME = '批次下載';
const DEFAULT_STALL_MS = 120_000;
const DEFAULT_STALL_CHECK_MS = 10_000;
// zip-stream.ts 遇到同名檔案時丟出的錯誤訊息
const DUPLICATE_NAME_MESSAGE = 'File already exists.';
const MAX_DESCRIBED_LENGTH = 200;

const describeValue = (value: unknown): string => {
  try {
    const text =
      typeof value === 'object' ? JSON.stringify(value) : String(value);
    return text.slice(0, MAX_DESCRIBED_LENGTH);
  } catch {
    return Object.prototype.toString.call(value);
  }
};

/**
 * 串流的 rejection 可以是任何值。非 Error 的值原樣交給 Sentry 會變成沒有 stack 的
 * synthetic 事件（NANI-BOX3-47 就是 `undefined`），所以一律轉成帶訊息的 Error。
 * 帶上 batchId 讓不同批次的訊息不同，Sentry 的 Dedupe 才不會把連續重試吃掉；
 * 分組由 fingerprint 決定，不受訊息影響。
 */
export const normalizeBatchZipError = (
  err: unknown,
  batchId?: string,
): { error: Error; kind: BatchZipErrorKind; originalErrorType: string } => {
  const originalErrorType = err === null ? 'null' : typeof err;
  const synthetic = () =>
    new Error(
      `batch zip stream failed${batchId ? ` (${batchId})` : ''}: ${describeValue(err)}`,
    );
  if (err === null || err === undefined) {
    return { error: synthetic(), kind: 'nullish', originalErrorType };
  }
  // DOMException 要先判斷：瀏覽器裡它也是 Error 的子類別
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    return { error: err, kind: 'dom-exception', originalErrorType };
  }
  if (err instanceof Error) {
    return { error: err, kind: 'error', originalErrorType };
  }
  const kind: BatchZipErrorKind =
    typeof err === 'string'
      ? 'string'
      : typeof err === 'object'
        ? 'object'
        : 'other-primitive';
  return { error: synthetic(), kind, originalErrorType };
};

/** 三層分類都空時原本會得到 false，檔名變成 false.zip */
export const resolveBatchDownloadName = (
  resource: Pick<TResource, 'resources1' | 'resources2' | 'resources3'>,
): string =>
  [
    resource.resources3?.[0],
    resource.resources2?.[0],
    resource.resources1?.[0],
  ].find(Boolean) || DEFAULT_DOWNLOAD_NAME;

type StreamSaverEnv = {
  HTMLElement?: unknown;
  safari?: unknown;
  WebKitPoint?: unknown;
  isSecureContext?: boolean;
  navigator?: object;
  Response?: typeof Response;
  ReadableStream?: typeof ReadableStream;
};

/**
 * StreamSaver 選哪種存檔方式是套件內部的私有變數，這裡在外面重算一次：
 * - blob：條件照抄 StreamSaver.js 第 23 行與第 94～100 行
 * - transferable：通過測試時套件才會在自己身上定義 TransformStream（StreamSaver.js 第 112 行）
 * - 其餘是 fallback（逐段經 MessageChannel 傳給外部背景程式）
 */
export const detectStreamSaverMode = (
  streamSaver: object,
  env: StreamSaverEnv = globalThis as unknown as StreamSaverEnv,
): StreamSaverMode => {
  const usesBlobFallback = (() => {
    if (
      /constructor/i.test(String(env.HTMLElement)) ||
      !!env.safari ||
      !!env.WebKitPoint
    ) {
      return true;
    }
    try {
      if (!env.Response || !env.ReadableStream) return true;
      new env.Response(new env.ReadableStream());
      return (
        !!env.isSecureContext && !('serviceWorker' in (env.navigator || {}))
      );
    } catch {
      return true;
    }
  })();
  if (usesBlobFallback) return 'blob';
  return Object.prototype.hasOwnProperty.call(streamSaver, 'TransformStream')
    ? 'transferable'
    : 'fallback';
};

export type BatchZipFailureInfo = {
  stage: BatchZipStage;
  kind: BatchZipErrorKind;
  fingerprint: string[];
  extra: Record<string, unknown>;
};

export type BatchZipReporter = {
  started: (payload: BatchZipStartedPayload) => void;
  terminal: (payload: BatchZipTerminalPayload) => void;
  failure: (error: Error, info: BatchZipFailureInfo) => void;
  fileFailed: (
    payload: BatchZipFileFailedPayload,
    extra: Record<string, unknown>,
  ) => void;
  stalled: (
    payload: BatchZipStalledPayload,
    extra: Record<string, unknown>,
  ) => void;
};

type LifecycleTarget = Pick<
  EventTarget,
  'addEventListener' | 'removeEventListener'
>;

type BatchZipLifecycle = {
  document?: LifecycleTarget & { visibilityState?: string };
  window?: LifecycleTarget;
};

export type BatchZipObserverOptions = {
  batchId: string;
  downloadName: string;
  list: TDownloadableResource[];
  streamSaverMode: StreamSaverMode;
  reporter: BatchZipReporter;
  /** 多久沒有任何讀寫進展就回報一次停滯 */
  stallMs?: number;
  stallCheckMs?: number;
  lifecycle?: BatchZipLifecycle;
  now?: () => number;
};

const defaultLifecycle = (): BatchZipLifecycle =>
  typeof document === 'undefined' ? {} : { document, window };

export type BatchZipObserver = ReturnType<typeof createBatchZipObserver>;

export const createBatchZipObserver = ({
  batchId,
  downloadName,
  list,
  streamSaverMode,
  reporter,
  stallMs = DEFAULT_STALL_MS,
  stallCheckMs = DEFAULT_STALL_CHECK_MS,
  lifecycle = defaultLifecycle(),
  now = Date.now,
}: BatchZipObserverOptions) => {
  const startedAt = now();
  let stage: BatchZipStage = 'read';
  let lastProgressAt = startedAt;
  let terminated = false;
  let stallReported = false;
  let stallTimer: ReturnType<typeof setInterval> | undefined;
  // 打包程式開始讀某個檔案時只給檔名，用這張表對回清單裡的序號
  const indexByName = new Map<string, number>();

  const counters = {
    /** 已交給打包程式。不代表內容已經讀完 */
    filesStarted: 0,
    /** 內容讀完、該檔的結尾也寫出。不代表已經寫進硬碟 */
    filesCompleted: 0,
    currentFileIndex: -1,
    currentFileName: '',
    currentFileBytesRead: 0,
    /** writer.write() 呼叫前累加 */
    bytesWriteAttempted: 0,
    /** writer.write() resolve 後才累加。不叫 bytesWritten，那會讓人誤以為已經寫進硬碟 */
    bytesAcceptedByWriter: 0,
    preEnqueueFailures: 0,
    httpFailures: 0,
    nullBodies: 0,
    duplicateNames: 0,
  };

  const page: {
    lastHiddenAt?: number;
    lastVisibilityChangeAt?: number;
    pagehideAt?: number;
    pagehidePersisted?: boolean;
  } = {};

  // 觀測不能影響下載：計數或回報本身出錯一律吞掉
  const safely = (fn: () => void) => {
    try {
      fn();
    } catch (error) {
      console.error('[batch-zip-observer]', error);
    }
  };

  const elapsed = () => now() - startedAt;
  const visibilityState = () => lifecycle.document?.visibilityState;

  // 只記錄時間點，不據此判定「使用者自己取消」：能回報出來代表頁面還活著，
  // 真的關掉分頁的多半送不出來，這份資料對那一類有選擇偏誤
  const onVisibilityChange = () =>
    safely(() => {
      const at = elapsed();
      page.lastVisibilityChangeAt = at;
      if (visibilityState() === 'hidden') page.lastHiddenAt = at;
    });
  const onPageHide = (event: Event) =>
    safely(() => {
      page.pagehideAt = elapsed();
      page.pagehidePersisted = (event as PageTransitionEvent).persisted;
    });

  const snapshot = (): Record<string, unknown> => ({
    batchId,
    downloadName,
    streamSaverMode,
    fileCount: list.length,
    stage,
    ...counters,
    durationMs: elapsed(),
    visibilityState: visibilityState(),
    lastHiddenAt: page.lastHiddenAt,
    pagehideAt: page.pagehideAt,
    pagehidePersisted: page.pagehidePersisted,
    timeSinceLastVisibilityChangeMs:
      page.lastVisibilityChangeAt === undefined
        ? undefined
        : elapsed() - page.lastVisibilityChangeAt,
  });

  const markProgress = () => {
    lastProgressAt = now();
  };

  const stopStallTimer = () => {
    if (stallTimer !== undefined) clearInterval(stallTimer);
    stallTimer = undefined;
  };

  // 只回報、不中止：網路慢的時候一段時間沒有進展不一定是卡住
  const checkStall = () =>
    safely(() => {
      if (terminated || stallReported) return;
      const idle = now() - lastProgressAt;
      if (idle < stallMs) return;
      stallReported = true;
      stopStallTimer();
      reporter.stalled(
        {
          batch_id: batchId,
          stage,
          current_file_index: counters.currentFileIndex,
          bytes_accepted: counters.bytesAcceptedByWriter,
          ms_since_last_progress: idle,
          visibility_state: visibilityState(),
        },
        snapshot(),
      );
    });

  // 卡住的批次永遠走不到這裡，它的監聽會留到頁面關閉，每批只有兩個，可以接受
  const finish = () => {
    terminated = true;
    stopStallTimer();
    safely(() => {
      lifecycle.document?.removeEventListener(
        'visibilitychange',
        onVisibilityChange,
      );
      lifecycle.window?.removeEventListener('pagehide', onPageHide);
    });
  };

  const terminalCounts = () => ({
    files_started: counters.filesStarted,
    files_completed: counters.filesCompleted,
    http_failures: counters.httpFailures,
    bytes_accepted: counters.bytesAcceptedByWriter,
    duration_ms: elapsed(),
    visibility_state: visibilityState(),
  });

  return {
    /** 要在點擊的同一個呼叫裡執行，started 才能在頁面還活著的時候送出 */
    start: () => {
      safely(() => {
        lifecycle.document?.addEventListener(
          'visibilitychange',
          onVisibilityChange,
        );
        lifecycle.window?.addEventListener('pagehide', onPageHide);
      });
      stallTimer = setInterval(checkStall, stallCheckMs);
      safely(() => {
        const first = list[0]?.resource;
        reporter.started({
          batch_id: batchId,
          file_count: list.length,
          total_bytes: list.reduce(
            (sum, { resource }) => sum + (resource.file.fileSize || 0),
            0,
          ),
          stream_saver_mode: streamSaverMode,
          edu: first?.eduCodes?.[0],
          subject: first?.subjects?.[0],
          category: first?.resources1?.[0],
        });
      });
    },
    /**
     * 要在交給打包程式之前呼叫：第一個檔案交出去的同時，打包程式就會通知開始讀它，
     * 晚登記會查不到序號。同名檔案會被打包程式拒收，不能蓋掉第一個檔案的登記。
     */
    onEnqueue: (index: number, fileName: string) =>
      safely(() => {
        if (!indexByName.has(fileName)) indexByName.set(fileName, index);
      }),
    onEnqueued: () =>
      safely(() => {
        counters.filesStarted += 1;
        markProgress();
      }),
    onHttpFailure: () =>
      safely(() => {
        counters.httpFailures += 1;
      }),
    /** 要在當下回報：現行行為下之後串流會卡住，批次層級的回報永遠送不出去 */
    onFileFailed: (
      index: number,
      fileName: string,
      reason: BatchZipFileFailReason,
      err?: unknown,
    ) =>
      safely(() => {
        if (reason === 'null_body') counters.nullBodies += 1;
        else if (reason === 'duplicate_name') counters.duplicateNames += 1;
        else counters.preEnqueueFailures += 1;
        const normalized =
          err === undefined ? undefined : normalizeBatchZipError(err, batchId);
        reporter.fileFailed(
          {
            batch_id: batchId,
            file_index: index,
            reason,
            files_completed: counters.filesCompleted,
          },
          {
            ...snapshot(),
            failedFileName: fileName,
            errorKind: normalized?.kind,
            errorMessage: normalized?.error.message,
          },
        );
      }),
    onFileStart: (fileName: string) =>
      safely(() => {
        counters.currentFileIndex = indexByName.get(fileName) ?? -1;
        counters.currentFileName = fileName;
        counters.currentFileBytesRead = 0;
        markProgress();
      }),
    onChunk: (length: number) =>
      safely(() => {
        counters.currentFileBytesRead += length;
        markProgress();
      }),
    onFileComplete: () =>
      safely(() => {
        counters.filesCompleted += 1;
        markProgress();
      }),
    onStage: (next: BatchZipStage) => {
      stage = next;
    },
    onWriteAttempt: (length: number) =>
      safely(() => {
        counters.bytesWriteAttempted += length;
      }),
    onWriteAccepted: (length: number) =>
      safely(() => {
        counters.bytesAcceptedByWriter += length;
        markProgress();
      }),
    succeed: () => {
      if (terminated) return;
      const counts = terminalCounts();
      finish();
      safely(() =>
        reporter.terminal({
          batch_id: batchId,
          outcome: counters.httpFailures > 0 ? 'partial' : 'success',
          ...counts,
        }),
      );
    },
    /** 回傳正規化後的 Error，讓呼叫端拿去中止讀寫兩端 */
    fail: (err: unknown): Error => {
      const normalized = normalizeBatchZipError(err, batchId);
      if (terminated) return normalized.error;
      const failureStage = stage;
      const counts = terminalCounts();
      const extra = {
        ...snapshot(),
        visibilityStateAtFailure: visibilityState(),
        originalErrorType: normalized.originalErrorType,
        errorKind: normalized.kind,
      };
      finish();
      safely(() =>
        reporter.terminal({
          batch_id: batchId,
          outcome: 'failure',
          stage: failureStage,
          error_kind: normalized.kind,
          ...counts,
        }),
      );
      safely(() =>
        reporter.failure(normalized.error, {
          stage: failureStage,
          kind: normalized.kind,
          fingerprint: ['multipleFileDownload', failureStage, normalized.kind],
          extra,
        }),
      );
      return normalized.error;
    },
  };
};

type BatchZipFetch = (
  url: string,
) => Promise<Pick<Response, 'ok' | 'status' | 'body'>>;

type ZipEntry = { name: string; stream: () => ReadableStream<Uint8Array> };

type ZipUnderlyingSource = {
  pull: (ctrl: {
    enqueue: (entry: ZipEntry) => void;
    close: () => void;
  }) => Promise<void>;
  onFileStart: (fileName: string) => void;
  onChunk: (length: number) => void;
  onFileComplete: (fileName: string) => void;
};

const createZip = ZIP as unknown as (
  source: ZipUnderlyingSource,
) => ReadableStream<Uint8Array>;

// fetch 還沒回來就失敗，是抓不到；回來之後的失敗來自交給打包程式這一步
const classifyFileFailure = (
  err: unknown,
  fetched: boolean,
): BatchZipFileFailReason => {
  if (!fetched) return 'fetch_reject';
  if (err instanceof Error && err.message === DUPLICATE_NAME_MESSAGE) {
    return 'duplicate_name';
  }
  return 'other';
};

export const createObservedZipStream = ({
  list,
  fetchFn,
  observer,
}: {
  list: TDownloadableResource[];
  fetchFn: BatchZipFetch;
  observer: BatchZipObserver;
}) => {
  const iterator = list[Symbol.iterator]();
  let nextIndex = 0;
  return createZip({
    // 控制流程刻意與觀測前相同：fetch 或交給打包程式失敗時，這一輪 pull 什麼都沒交出去，
    // 打包程式不會再來要下一個檔案，串流就停住（NANI-BOX3-47 的已確認路徑）。
    // 修正屬於方案 B1，這裡只加回報。
    async pull(ctrl) {
      const file = iterator.next();
      if (file.done) {
        ctrl.close();
        return;
      }
      const index = nextIndex;
      nextIndex += 1;
      const { url, fileName } = file.value.resource.file;
      let fetched = false;
      try {
        const res = await fetchFn(url);
        fetched = true;
        // 現行會把錯誤頁當成檔案內容打包，只計數、不改行為
        if (!res.ok) observer.onHttpFailure();
        const { body } = res;
        if (body) {
          observer.onEnqueue(index, fileName);
          ctrl.enqueue({ name: fileName, stream: () => body });
          observer.onEnqueued();
        } else {
          console.error('Fetch response body is null.');
          observer.onFileFailed(index, fileName, 'null_body');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        observer.onFileFailed(
          index,
          fileName,
          classifyFileFailure(err, fetched),
          err,
        );
      }
    },
    onFileStart: observer.onFileStart,
    onChunk: observer.onChunk,
    onFileComplete: observer.onFileComplete,
  });
};

type BatchZipWriter = {
  write: (chunk: Uint8Array) => Promise<void>;
  close: () => Promise<void>;
  abort: (reason?: unknown) => Promise<void>;
};

/**
 * 邊讀打包結果邊寫出，並記錄目前在哪一段（讀／寫／收尾），失敗時才知道斷在哪。
 * 回傳的 promise 不會 reject；卡住的批次則永遠不會結束，呼叫端不要 await 它。
 */
export const runObservedBatchZip = ({
  list,
  fetchFn,
  writer,
  observer,
}: {
  list: TDownloadableResource[];
  fetchFn: BatchZipFetch;
  writer: BatchZipWriter;
  observer: BatchZipObserver;
}): Promise<void> => {
  observer.start();
  const reader = createObservedZipStream({
    list,
    fetchFn,
    observer,
  }).getReader();

  const pump = (): Promise<void> => {
    observer.onStage('read');
    return reader.read().then(({ done, value }) => {
      if (done) {
        observer.onStage('close');
        return writer.close();
      }
      observer.onStage('write');
      observer.onWriteAttempt(value.length);
      return writer.write(value).then(() => {
        observer.onWriteAccepted(value.length);
        return pump();
      });
    });
  };

  return pump().then(
    () => observer.succeed(),
    (err: unknown) => {
      const error = observer.fail(err);
      // 讀寫兩端一起收：只中止 writer 的話，打包程式與進行中的回應仍占著資源
      void reader.cancel(error).catch(() => {});
      void writer.abort(error).catch(() => {});
    },
  );
};
